import { superAdminApi } from "../authService";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../../types/api";

const RESOURCE = "/super-admin/blog";

export type BlogPostStatus =
  | "draft"
  | "pending_review"
  | "published"
  | "rejected"
  | "localization_failed";

export type BlogLocaleSiblingStatus =
  | "published"
  | "failed"
  | "missing"
  | "pending";

export interface BlogLocaleSiblings {
  en: BlogLocaleSiblingStatus;
  es: BlogLocaleSiblingStatus;
}

export interface BlogPostApprover {
  id: number;
  name: string | null;
  email: string;
}

export interface BlogFaqItem {
  question: string;
  answer: string;
}

export interface BlogSeoCheck {
  key: string;
  pass: boolean;
  detail: string;
}

export interface BlogSeoScore {
  score: number;
  max: number;
  grade: string;
  checks: BlogSeoCheck[];
}

export interface BlogRelatedOption {
  id: number;
  title: string;
  slug: string;
  status: BlogPostStatus;
}

export interface BlogDiffField {
  field: string;
  before: string | null;
  after: string | null;
  changed: boolean;
}

export interface BlogDiffResult {
  has_snapshot: boolean;
  fields: BlogDiffField[];
}

export interface BlogPost {
  id: number;
  status: BlogPostStatus;
  locale?: string;
  translation_group_id?: string;
  locale_siblings?: BlogLocaleSiblings | null;
  slug: string;
  title: string;
  excerpt: string | null;
  meta_title: string | null;
  meta_description: string | null;
  body_md: string;
  cover_path: string | null;
  cover_url: string | null;
  cover_prompt: string | null;
  source_feature_keys: string[];
  seo_keywords: string[];
  faq_json: BlogFaqItem[];
  related_post_ids: number[];
  related_options?: BlogRelatedOption[];
  topic_key: string | null;
  rejection_reason: string | null;
  approved_by: number | null;
  approver?: BlogPostApprover | null;
  published_at: string | null;
  rejected_at: string | null;
  canonical_url: string;
  has_published_snapshot?: boolean;
  seo_score?: BlogSeoScore;
  generation_tokens: number | null;
  generation_model: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlogPostListParams extends PaginationParams {
  status?: BlogPostStatus | "";
  search?: string;
}

export interface BlogPostListMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  pending_review_count?: number;
}

export interface UpdateBlogPostPayload {
  title?: string;
  slug?: string;
  excerpt?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  body_md?: string;
  cover_prompt?: string | null;
  source_feature_keys?: string[];
  seo_keywords?: string[];
  faq_json?: BlogFaqItem[];
  related_post_ids?: number[];
}

export interface BlogTopicCatalogItem {
  key: string;
  angle: string;
  feature_keys: string[];
  resource_paths: string[];
  in_queue: boolean;
  sort_order: number | null;
  recently_used: boolean;
}

export interface BlogTopicQueueItem {
  id: number;
  topic_key: string;
  sort_order: number;
  angle: string | null;
}

export const getBlogPosts = async (
  params?: BlogPostListParams
): Promise<PaginatedApiResponse<BlogPost> & { meta: BlogPostListMeta }> => {
  const response = await superAdminApi.get<
    PaginatedApiResponse<BlogPost> & { meta: BlogPostListMeta }
  >(RESOURCE, { params });
  return response.data;
};

export const getBlogSummary = async (): Promise<
  ApiResponse<{ pending_review_count: number }>
> => {
  const response = await superAdminApi.get<
    ApiResponse<{ pending_review_count: number }>
  >(`${RESOURCE}/summary`);
  return response.data;
};

export const getBlogTopics = async (): Promise<
  ApiResponse<{ catalog: BlogTopicCatalogItem[]; queue: BlogTopicQueueItem[] }>
> => {
  const response = await superAdminApi.get<
    ApiResponse<{ catalog: BlogTopicCatalogItem[]; queue: BlogTopicQueueItem[] }>
  >(`${RESOURCE}/topics`);
  return response.data;
};

export const replaceBlogTopicQueue = async (
  topicKeys: string[]
): Promise<
  ApiResponse<{ catalog: BlogTopicCatalogItem[]; queue: BlogTopicQueueItem[] }>
> => {
  const response = await superAdminApi.put<
    ApiResponse<{ catalog: BlogTopicCatalogItem[]; queue: BlogTopicQueueItem[] }>
  >(`${RESOURCE}/topics/queue`, { topic_keys: topicKeys });
  return response.data;
};

export const previewBlogMarkdown = async (
  bodyMd: string
): Promise<ApiResponse<{ html: string }>> => {
  const response = await superAdminApi.post<ApiResponse<{ html: string }>>(
    `${RESOURCE}/preview`,
    { body_md: bodyMd }
  );
  return response.data;
};

export const getBlogPost = async (
  id: number
): Promise<ApiResponse<BlogPost>> => {
  const response = await superAdminApi.get<ApiResponse<BlogPost>>(
    `${RESOURCE}/${id}`
  );
  return response.data;
};

export const updateBlogPost = async (
  id: number,
  payload: UpdateBlogPostPayload
): Promise<ApiResponse<BlogPost>> => {
  const response = await superAdminApi.patch<ApiResponse<BlogPost>>(
    `${RESOURCE}/${id}`,
    payload
  );
  return response.data;
};

export const approveBlogPost = async (
  id: number
): Promise<ApiResponse<BlogPost>> => {
  const response = await superAdminApi.post<ApiResponse<BlogPost>>(
    `${RESOURCE}/${id}/approve`
  );
  return response.data;
};

export const rejectBlogPost = async (
  id: number,
  reason: string
): Promise<ApiResponse<BlogPost>> => {
  const response = await superAdminApi.post<ApiResponse<BlogPost>>(
    `${RESOURCE}/${id}/reject`,
    { reason }
  );
  return response.data;
};

export const unpublishBlogPost = async (
  id: number
): Promise<ApiResponse<BlogPost>> => {
  const response = await superAdminApi.post<ApiResponse<BlogPost>>(
    `${RESOURCE}/${id}/unpublish`
  );
  return response.data;
};

export const regenerateBlogCover = async (
  id: number
): Promise<ApiResponse<BlogPost>> => {
  const response = await superAdminApi.post<ApiResponse<BlogPost>>(
    `${RESOURCE}/${id}/regenerate-cover`
  );
  return response.data;
};

export const regenerateBlogSeo = async (
  id: number
): Promise<ApiResponse<BlogPost>> => {
  const response = await superAdminApi.post<ApiResponse<BlogPost>>(
    `${RESOURCE}/${id}/regenerate-seo`
  );
  return response.data;
};

export const getBlogPostDiff = async (
  id: number
): Promise<ApiResponse<BlogDiffResult>> => {
  const response = await superAdminApi.get<ApiResponse<BlogDiffResult>>(
    `${RESOURCE}/${id}/diff`
  );
  return response.data;
};

export const downloadBlogExportCsv = async (): Promise<Blob> => {
  const response = await superAdminApi.get(`${RESOURCE}/export.csv`, {
    responseType: "blob",
  });
  return response.data;
};

export const generateBlogPostNow = async (): Promise<ApiResponse<null>> => {
  const response = await superAdminApi.post<ApiResponse<null>>(
    `${RESOURCE}/generate`
  );
  return response.data;
};
